// Drop-in replacement for the Claude-artifact `window.storage` API, backed by
// our own Express server instead. Same method shapes (get/set returning
// { value }), so App.jsx needed zero changes for its storage calls.

const KEY_TO_ROUTE = {
  "fleet-drivers-v4": "drivers",
  "fleet-earnings-v4": "earnings",
  "fleet-global-bonuses-v1": "bonuses",
  "fleet-avatars-v1": "avatars",
  "fleet-payout-decisions-v1": "payout-decisions",
  "fleet-compensation-requests-v1": "compensation-requests",
  "fleet-chat-threads-v1": "chat-threads",
  "fleet-news-v1": "news",
  "fleet-tasks-v1": "tasks",
  "fleet-point-adjustments-v1": "point-adjustments",
  "fleet-cars-v1": "cars",
  "fleet-weekly-revenue-v1": "weekly-revenue",
  "fleet-profit-loss-v1": "profit-loss",
  "fleet-car-repairs-v1": "car-repairs",
  "fleet-company-expenses-v1": "company-expenses",
  "fleet-car-info-v1": "car-info",
  "fleet-company-adjustments-v1": "company-adjustments",
  "fleet-crypto-paper-portfolio-v1": "crypto-paper-portfolio",
  "fleet-crypto-strategy-params-v1": "crypto-strategy-params",
  "fleet-crypto-intraday-portfolio-v1": "crypto-intraday-portfolio",
  "fleet-crypto-intraday-strategy-params-v1": "crypto-intraday-strategy-params",
  "fleet-debt-repayments-v1": "debt-repayments",
  "fleet-driver-debts-v1": "driver-debts",
  "fleet-group-chat-v1": "group-chat-messages",
  "fleet-dm-threads-v1": "dm-threads",
};

// Odczyt z serwera z zabezpieczeniem na słaby zasięg. Wcześniej zwykły
// fetch() bez limitu czasu: jedno zawieszone połączenie (typowe w telefonie
// w samochodzie) blokowało start appki NA ZAWSZE — kierowca widział tylko
// ekran ładowania/czarny ekran. Teraz każda próba ma limit czasu, a nieudany
// odczyt jest ponawiany (2x) na świeżym połączeniu. Jeśli wszystkie próby
// zawiodą, zwracamy null — dokładnie jak wcześniej przy błędzie sieci.
const ATTEMPT_TIMEOUTS_MS = [12000, 20000, 30000]; // rosnące, żeby wolne (ale działające) łącze zdążyło
const RETRY_DELAYS_MS = [400, 1500];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchRouteData(route) {
  for (let attempt = 0; attempt < ATTEMPT_TIMEOUTS_MS.length; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ATTEMPT_TIMEOUTS_MS[attempt]);
    try {
      const res = await fetch(`/api/${route}`, { signal: controller.signal });
      if (res.ok) {
        const data = await res.json();
        clearTimeout(timer);
        return { ok: true, data, version: res.headers.get("X-Collection-Version") || null };
      }
      clearTimeout(timer);
      // Błąd "po naszej stronie" (np. 404) nie zniknie po ponowieniu — nie męcz serwera.
      if (res.status < 500 && res.status !== 408 && res.status !== 429) return { ok: false };
    } catch (e) {
      clearTimeout(timer); // przerwane przez limit czasu albo błąd sieci — spróbuj ponownie
    }
    if (attempt < ATTEMPT_TIMEOUTS_MS.length - 1) await sleep(RETRY_DELAYS_MS[attempt]);
  }
  return { ok: false };
}

// strict=true: nieudany odczyt RZUCA błąd zamiast zwracać null. Używane tam,
// gdzie "brak danych" oznaczałby pustą listę i groził jej zapisaniem/
// wyświetleniem zamiast prawdziwych danych (dopisywanie do czatu, odświeżanie).
async function readRoute(key, route, strict) {
  const result = await fetchRouteData(route);
  if (!result.ok) {
    if (strict) throw new Error(`Nie udało się pobrać danych (${route}) — sprawdź połączenie z internetem.`);
    return null;
  }
  return { key, value: JSON.stringify(result.data), shared: true, version: result.version };
}

// Start appki czytał ~24 kolekcji JEDNA PO DRUGIEJ (App.jsx robi `await` na
// każdej z nich po kolei) — przy ~200-300 ms na zapytanie to kilka sekund
// przy dobrym zasięgu, a przy słabym dużo dłużej. Dlatego już TERAZ, zanim
// appka w ogóle się uruchomi, rozpoczynamy pobieranie wszystkich tych
// samych kolekcji równolegle (max 6 naraz — tyle połączeń przeglądarka
// dopuszcza do jednego serwera). Kiedy App.jsx zapyta później o daną
// kolekcję po raz pierwszy, dostaje wynik, który już jest w drodze albo
// gotowy. Każdy wynik można odebrać tylko RAZ i tylko przez ograniczony czas
// (PREFETCH_MAX_AGE_MS) — kolejne odczyty (odświeżanie w tle) zawsze idą do
// serwera po świeże dane, tak jak wcześniej.
const PREFETCH_ROUTES = [
  "drivers",
  "earnings",
  "bonuses",
  "avatars",
  "payout-decisions",
  "compensation-requests",
  "chat-threads",
  "news",
  "tasks",
  "point-adjustments",
  "cars",
  "weekly-revenue",
  "profit-loss",
  "car-repairs",
  "company-expenses",
  "car-info",
  "company-adjustments",
  "crypto-paper-portfolio",
  "crypto-strategy-params",
  "crypto-intraday-portfolio",
  "crypto-intraday-strategy-params",
  "debt-repayments",
  "driver-debts",
  "group-chat-messages",
];
const PREFETCH_CONCURRENCY = 6;
const PREFETCH_MAX_AGE_MS = 120000;

const prefetched = new Map(); // route -> { promise, startedAt }
const routeToKey = {};
for (const [key, route] of Object.entries(KEY_TO_ROUTE)) routeToKey[route] = key;

function startPrefetch() {
  try {
    const queue = [];
    for (const route of PREFETCH_ROUTES) {
      let resolveFn;
      const promise = new Promise((resolve) => {
        resolveFn = resolve;
      });
      prefetched.set(route, { promise, startedAt: Date.now() });
      queue.push({ route, resolveFn });
    }
    let next = 0;
    async function worker() {
      while (next < queue.length) {
        const { route, resolveFn } = queue[next++];
        try {
          resolveFn(await readRoute(routeToKey[route], route));
        } catch (e) {
          resolveFn(null);
        }
      }
    }
    for (let i = 0; i < PREFETCH_CONCURRENCY; i++) worker();
  } catch (e) {
    prefetched.clear(); // w razie czegokolwiek nieoczekiwanego — wróć do zwykłego zachowania
  }
}
startPrefetch();

// Zapis z wykrywaniem konfliktów (opts.ifVersion = wersja kolekcji, na której
// oparto zmianę) — serwer odrzuca go (412), jeśli ktoś inny zdążył w
// międzyczasie zapisać tę kolekcję, zamiast po cichu nadpisać jego zmiany.
// Wtedy rzucamy błąd z conflict=true, a wywołujący pobiera dane od nowa i
// dokłada swoją zmianę jeszcze raz. Zapisy BEZ opts działają jak dawniej.
const CONFLICT_PUT_TIMEOUT_MS = 45000; // żeby zawieszony zapis nie blokował kolejki wiadomości na zawsze

window.storage = {
  async get(key, shared, opts) {
    const strict = !!(opts && opts.strict);
    const route = KEY_TO_ROUTE[key];
    if (!route) return null; // e.g. the old seed-marker key — no longer needed
    // Odczyt "ścisły" (baza dla zmiany/zapisu) ZAWSZE idzie po świeże dane — nigdy z prefetchu.
    const early = strict ? null : prefetched.get(route);
    if (early) {
      prefetched.delete(route); // jednorazowe — następny odczyt tej kolekcji idzie już do serwera
      if (Date.now() - early.startedAt <= PREFETCH_MAX_AGE_MS) return early.promise;
    }
    return readRoute(key, route, strict);
  },
  async set(key, value, shared, opts) {
    const route = KEY_TO_ROUTE[key];
    if (!route) return null;
    const ifVersion = opts && opts.ifVersion;
    const controller = ifVersion ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CONFLICT_PUT_TIMEOUT_MS) : null;
    try {
      const res = await fetch(`/api/${route}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(ifVersion ? { "X-If-Version": ifVersion } : {}) },
        body: value,
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!res.ok) {
        if (res.status === 412 && ifVersion) {
          const conflict = new Error(`Kolekcja "${route}" zmieniła się w międzyczasie.`);
          conflict.conflict = true;
          throw conflict;
        }
        try {
          const errBody = await res.json();
          console.error(`[storage] zapis "${route}" odrzucony (${res.status}):`, errBody.error || errBody);
        } catch (e) {
          console.error(`[storage] zapis "${route}" odrzucony (${res.status})`);
        }
        return null;
      }
      return { key, value, shared: true, version: res.headers.get("X-Collection-Version") || null };
    } catch (e) {
      if (e && e.conflict) throw e;
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  },
  async delete(key) {
    return null; // not used by this app
  },
  async list() {
    return null; // not used by this app
  },
};
